import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const InputContainer = styled.div`
  position: relative;
  padding: 16px;
  background-color: var(--clr-bg-secondary, #2a2a2a);
  flex-shrink: 0; /* Prevent shrinking when keyboard appears */
`;

const ReplyPreview = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-left: 3px solid ${(props) => props.$replyColor || 'var(--clr-primary, #007bff)'};
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  border-radius: 0 6px 6px 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
`;

const ReplyContent = styled.div`
  flex: 1;
  min-width: 0; /* Critical for text truncation in flex children */
  display: flex;
  flex-direction: column;
`;

const ReplyHeader = styled.div`
  font-size: 11px;
  color: ${(props) => props.$replyColor || 'var(--clr-primary, #007bff)'};
  font-weight: 500;
  margin-bottom: 2px;
`;

const ReplyText = styled.div`
  font-size: 12px;
  color: var(--clr-text-secondary, #aaa);
  opacity: 0.8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const ReplyCloseButton = styled.button`
  background: none;
  border: none;
  color: var(--clr-text-secondary, #aaa);
  opacity: 0.6;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: opacity 0.2s ease, background-color 0.2s ease;
  font-size: 14px;
  min-width: 24px;
  min-height: 24px;
  flex-shrink: 0;
  margin-left: auto;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  max-width: 1000px;
  margin: 0 auto;
`;

const InputField = styled.textarea`
  flex: 1;
  background-color: var(--clr-bg, #1a1a1a);
  border: 1px solid var(--clr-border, #444);
  border-radius: 20px;
  padding: 10px 16px;
  color: var(--clr-text, #ffffff);
  font-size: 0.95rem;
  font-family: inherit;
  resize: none;
  max-height: 120px;
  min-height: 40px;
  line-height: 1.4;

  &::placeholder {
    color: var(--clr-text-secondary, #aaa);
  }

  &:focus {
    outline: none;
    border-color: var(--clr-primary, #007bff);
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--clr-border, #444);
    border-radius: 3px;
  }
`;

const SendButton = styled.button`
  background-color: var(--clr-primary, #007bff);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 1.1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  transform-origin: center;

  &:hover:not(:disabled) {
    background-color: var(--clr-primary-hover, #0056b3);
    transform: scale(1.15) translateY(-2px);
    filter: drop-shadow(0 6px 12px rgba(0, 123, 255, 0.3));
    color: #ffffff;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
    transition: all 0.1s ease;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    filter: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: flex-end;
  flex-shrink: 0;
  gap: 4px;
`;

const CharCount = styled.div`
  font-size: 0.75rem;
  color: ${(props) =>
    props.$nearLimit
      ? 'var(--clr-warning, #ffa500)'
      : 'var(--clr-text-secondary, #aaa)'};
  text-align: center;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transition: opacity 0.2s ease;
  min-height: 18px;
`;

function DMInputBar({
  onSendMessage,
  disabled = false,
  replyToMessage = null,
  onClearReply,
}) {
  const [message, setMessage] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const maxLength = 300;

  // Focus the input when a reply is initiated from the parent
  useEffect(() => {
    if (replyToMessage && inputRef.current) {
      inputRef.current.focus();
    }
  }, [replyToMessage?.id, replyToMessage]);

  const clearReply = () => {
    if (onClearReply) onClearReply();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!message.trim() || disabled) {
      return;
    }

    // Pass full reply information when replying, mirroring room chat
    onSendMessage({
      messageText: message,
      replyToId: replyToMessage?.id || null,
      replyToText: replyToMessage?.messageText || null,
      replyToNickname:
        replyToMessage?.sender?.nickname || replyToMessage?.nickname || null,
      replyToUserId:
        replyToMessage?.sender?.id || replyToMessage?.userId || null,
      replyToColor: replyToMessage?.color || null,
    });
    setMessage('');
    clearReply();

    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxLength) {
      setMessage(value);

      // Auto-resize textarea
      const textarea = e.target;
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
  };

  const canSend = message.trim().length > 0 && !disabled;
  const nearLimit = message.length > maxLength * 0.8;

  return (
    <InputContainer ref={containerRef}>
      {replyToMessage && (
        <ReplyPreview $replyColor={replyToMessage.color}>
          <ReplyContent>
            <ReplyHeader $replyColor={replyToMessage.color}>
              ↳ Replying to{' '}
              {replyToMessage.sender?.nickname ||
                replyToMessage.nickname ||
                'Anon'}
            </ReplyHeader>
            <ReplyText>{replyToMessage.messageText}</ReplyText>
          </ReplyContent>
          <ReplyCloseButton onClick={clearReply} title="Cancel reply">
            ✕
          </ReplyCloseButton>
        </ReplyPreview>
      )}
      <InputWrapper>
        <InputField
          ref={inputRef}
          value={message}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={disabled ? 'Cannot send messages' : 'Type a message...'}
          disabled={disabled}
          maxLength={maxLength}
          autoComplete="off"
          autoCorrect="on"
          autoCapitalize="sentences"
          spellCheck="true"
          inputMode="text"
          enterKeyHint="send"
        />
        <ButtonContainer>
          <SendButton
            onClick={handleSubmit}
            disabled={!canSend}
            title='Send message (Enter)'
          >
            ➤
          </SendButton>
          <CharCount $nearLimit={nearLimit} $visible={message.length > 0}>
            {message.length > 0 ? `${message.length}/${maxLength}` : ''}
          </CharCount>
        </ButtonContainer>
      </InputWrapper>
    </InputContainer>
  );
}

export default DMInputBar;
